import { useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports -- ここだけが Chakra の useToast を包む
import { useToast as useChakraToast } from '@chakra-ui/react';
import type { ToastId, UseToastOptions } from '@chakra-ui/react';
import { localizeErrorText } from '../lib/errorMessage';

let errorSeq = 0;
/** 出ているエラーの「タイトル・説明」→ id（画面をまたいで同じ文言を重ねないため） */
const activeErrorIds = new Map<string, ToastId>();

/**
 * アプリ共通のトースト（2026-10-03・UX 監査 X-2）。Chakra の useToast の代わりに使う。
 *
 * エラー（status: 'error'）だけ次のようにそろえる。それ以外はそのまま。
 * - 閉じるまで消えない（duration: null・閉じるボタンあり）。以前は 3 秒以内に消えるエラーが 41 か所あり、
 *   酔っていると読む前に消えて、何が起きたか分からないまま次の操作をしていた
 * - 画面の上、ヘッダーの下に出す（下に出すと、スマホの下部メニューやモーダルの「閉じる」「確定」を覆い続ける。
 *   ヘッダーに重ねると店舗選択を覆う）
 * - 英語だけの説明（「Request failed with status code 500」「Network Error」等）は日本語の決まり文句に置き換える
 * - 同じ文言のエラーは重ねない。ただし押し直したときに何も起きないように見えないよう、古い方を閉じて出し直す
 * - 成功の通知でエラーを閉じることはしない（大きな画面は 1 つのフックで裏の取り直しの通知も出すので、関係ない未読のエラーまで閉じてしまう）。
 *   やり直して成功した後の古いエラーは手で閉じる
 */
export function useToast(options?: UseToastOptions): ReturnType<typeof useChakraToast> {
  const toast = useChakraToast(options);
  return useMemo(() => {
    const show = (opts?: UseToastOptions) => {
      if (opts?.status !== 'error') return toast(opts);
      const title = typeof opts.title === 'string' ? (localizeErrorText(opts.title) ?? opts.title) : opts.title;
      const description = typeof opts.description === 'string' ? localizeErrorText(opts.description) : opts.description;
      const key = `${typeof title === 'string' ? title : ''}||${typeof description === 'string' ? description : ''}`;
      const prev = activeErrorIds.get(key);
      if (prev != null && toast.isActive(prev)) toast.close(prev);
      // 呼び出し側が id を決めているエラーが出ているなら、出し直さずに中身を差し替える（同じ id は重ねて出せない）
      if (opts.id != null && toast.isActive(opts.id)) {
        toast.update(opts.id, { ...opts, title, description, position: 'top',
        containerStyle: { ...(opts.containerStyle ?? {}), marginTop: '72px' }, duration: null, isClosable: true });
        return opts.id;
      }
      const id = opts.id ?? `app-error-${++errorSeq}`;
      activeErrorIds.set(key, id);
      return toast({
        ...opts,
        id,
        title,
        description,
        position: 'top',
        containerStyle: { ...(opts.containerStyle ?? {}), marginTop: '72px' },
        duration: null,
        isClosable: true,
        onCloseComplete: () => {
          if (activeErrorIds.get(key) === id) activeErrorIds.delete(key);
          opts.onCloseComplete?.();
        },
      });
    };
    return Object.assign(show, {
      close: toast.close,
      closeAll: toast.closeAll,
      update: toast.update,
      isActive: toast.isActive,
      promise: toast.promise,
    }) as unknown as ReturnType<typeof useChakraToast>;
  }, [toast]);
}

export default useToast;
